import {
  AlertOutlined,
  CalendarOutlined,
  RightOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Card, Segmented } from "antd";
import { FC, useState } from "react";
import { PROJECT_PHASE_LABELS, ProjectPhase } from "../../projectPhase";
import {
  collectProjectContentItems,
  ProjectContentItemFocus,
  ProjectContentTask,
} from "../ProjectContentItemList";
import TaskPriorityTag, { TaskPriority } from "../TaskPriorityTag";
import "./index.css";

interface DueRiskProject {
  project_id: string;
  project_name: string;
  template?: "phased" | "single";
}

export interface ContentDueRiskItem {
  assignees: string[];
  content: string;
  contentItemId: string;
  daysLeft: number;
  dueTime: number;
  parentTaskId?: string;
  parentTaskTitle: string;
  phase: ProjectPhase;
  priority: TaskPriority;
  projectId: string;
  projectName: string;
  projectTemplate: "phased" | "single";
  subtaskId?: string;
  subtaskTitle: string;
}

export type ContentDueRiskFocus = ProjectContentItemFocus;

interface ContentDueRiskPanelProps {
  getMemberDisplayName: (username: string) => string;
  includeAll: boolean;
  onOpenItem: (focus: ContentDueRiskFocus) => void;
  projects: DueRiskProject[];
  tasks: ProjectContentTask[];
  userId: string;
}

type RiskFilter = "all" | "overdue" | "today" | "upcoming";

const DAY_MS = 24 * 60 * 60 * 1000;

function getLocalCalendarDay(timestamp: number) {
  const date = new Date(timestamp);
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

export function getContentDueDayDistance(dueTime: number, now = Date.now()) {
  return Math.round(
    (getLocalCalendarDay(dueTime) - getLocalCalendarDay(now)) / DAY_MS
  );
}

export function collectContentDueRiskItems({
  includeAll,
  now = Date.now(),
  projects,
  tasks,
  userId,
}: {
  includeAll: boolean;
  now?: number;
  projects: DueRiskProject[];
  tasks: ProjectContentTask[];
  userId: string;
}) {
  return projects
    .flatMap((project) =>
      collectProjectContentItems(tasks, project.project_id, userId, true)
        .filter(
          (item) =>
            !item.completed &&
            (includeAll || item.assignees.includes(userId))
        )
        .map((item): ContentDueRiskItem => ({
          assignees: item.assignees,
          content: item.content,
          contentItemId: item.item_id,
          daysLeft: getContentDueDayDistance(item.due_time, now),
          dueTime: item.due_time,
          parentTaskId: item.parentTaskId,
          parentTaskTitle: item.parentTaskTitle,
          phase: item.phase,
          priority: item.priority,
          projectId: project.project_id,
          projectName: project.project_name,
          projectTemplate: project.template || "phased",
          subtaskId: item.subtaskId,
          subtaskTitle: item.subtaskTitle,
        }))
    )
    .filter((item) => item.daysLeft <= 2)
    .sort(
      (left, right) =>
        left.dueTime - right.dueTime ||
        left.projectName.localeCompare(right.projectName, "zh-CN")
    );
}

function getContentText(content: string) {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = content || "";
  return (
    (wrapper.textContent || "").replace(/\s+/g, " ").trim() ||
    (wrapper.querySelector("img") ? "图片内容" : "未填写内容")
  );
}

function formatDate(timestamp: number) {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestamp));
}

function getRiskState(daysLeft: number) {
  if (daysLeft < 0) {
    return {
      className: "is-overdue",
      label: `已逾期 ${Math.abs(daysLeft)} 天`,
    };
  }
  if (daysLeft === 0) {
    return { className: "is-today", label: "今天到期" };
  }
  if (daysLeft === 1) {
    return { className: "is-tomorrow", label: "明天到期" };
  }
  return { className: "is-upcoming", label: "后天到期" };
}

function getProjectTagTone(projectId: string) {
  let hash = 0;
  for (let index = 0; index < projectId.length; index += 1) {
    hash = (hash * 31 + projectId.charCodeAt(index)) >>> 0;
  }
  return `tone-${hash % 6}`;
}

const ContentDueRiskPanel: FC<ContentDueRiskPanelProps> = ({
  getMemberDisplayName,
  includeAll,
  onOpenItem,
  projects,
  tasks,
  userId,
}) => {
  const [filter, setFilter] = useState<RiskFilter>("all");
  const items = collectContentDueRiskItems({
    includeAll,
    projects,
    tasks,
    userId,
  });
  const overdueCount = items.filter((item) => item.daysLeft < 0).length;
  const todayCount = items.filter((item) => item.daysLeft === 0).length;
  const upcomingCount = items.filter((item) => item.daysLeft > 0).length;
  const visibleItems = items.filter((item) => {
    if (filter === "overdue") return item.daysLeft < 0;
    if (filter === "today") return item.daysLeft === 0;
    if (filter === "upcoming") return item.daysLeft > 0;
    return true;
  });

  return (
    <Card className="workspacePanel contentDueRiskPanel">
      <div className="contentDueRiskPanel__header">
        <div className="contentDueRiskPanel__heading">
          <span><AlertOutlined /></span>
          <div>
            <h3>到期风险</h3>
            <p>未来两天即将到期及仍未完成的逾期内容</p>
          </div>
        </div>
        <div className="contentDueRiskPanel__summary">
          <span className="is-overdue"><strong>{overdueCount}</strong> 已逾期</span>
          <span className="is-today"><strong>{todayCount}</strong> 今天到期</span>
          <span className="is-upcoming"><strong>{upcomingCount}</strong> 两天内</span>
        </div>
      </div>

      <div className="contentDueRiskPanel__toolbar">
        <Segmented
          onChange={(value) => setFilter(value as RiskFilter)}
          options={[
            { label: `全部 ${items.length}`, value: "all" },
            { label: `已逾期 ${overdueCount}`, value: "overdue" },
            { label: `今天 ${todayCount}`, value: "today" },
            { label: `即将到期 ${upcomingCount}`, value: "upcoming" },
          ]}
          value={filter}
        />
        <span>{includeAll ? "展示项目范围内全部负责人" : "仅展示我负责的内容"}</span>
      </div>

      {visibleItems.length ? (
        <div className="contentDueRiskPanel__list">
          {visibleItems.map((item) => {
            const state = getRiskState(item.daysLeft);
            const contentText = getContentText(item.content);
            const assigneeNames = item.assignees
              .map((assignee) => getMemberDisplayName(assignee) || assignee)
              .filter(Boolean);
            return (
              <button
                aria-label={`查看${item.projectName}中的${contentText}`}
                className={`contentDueRiskItem ${state.className}`}
                key={`${item.projectId}-${item.subtaskId}-${item.contentItemId}`}
                onClick={() =>
                  onOpenItem({
                    contentItemId: item.contentItemId,
                    projectId: item.projectId,
                    phase: item.phase,
                    taskId: item.parentTaskId,
                    parentTaskId: item.parentTaskId,
                    subtaskId: item.subtaskId,
                  })
                }
                type="button"
              >
                <div className="contentDueRiskItem__top">
                  <span className={`contentDueRiskItem__state ${state.className}`}>
                    <CalendarOutlined /> {state.label}
                  </span>
                  <TaskPriorityTag priority={item.priority} />
                </div>
                <strong className="contentDueRiskItem__content" title={contentText}>
                  {contentText}
                </strong>
                <div className="contentDueRiskItem__projectContext">
                  <span
                    className={`contentDueRiskItem__projectTag ${getProjectTagTone(
                      item.projectId
                    )}`}
                    title={item.projectName}
                  >
                    {item.projectName}
                  </span>
                  <span className="contentDueRiskItem__phaseTag">
                    {item.projectTemplate === "single"
                      ? "任务视图"
                      : PROJECT_PHASE_LABELS[item.phase]}
                  </span>
                </div>
                <div className="contentDueRiskItem__taskPath">
                  <div className="contentDueRiskItem__taskPathItem">
                    <span>任务模块</span>
                    <strong title={item.parentTaskTitle}>
                      {item.parentTaskTitle}
                    </strong>
                  </div>
                  <RightOutlined />
                  <div className="contentDueRiskItem__taskPathItem">
                    <span>子任务</span>
                    <strong title={item.subtaskTitle}>{item.subtaskTitle}</strong>
                  </div>
                </div>
                <footer>
                  <span title={assigneeNames.join("、")}>
                    <TeamOutlined /> {assigneeNames.join("、") || "未分配负责人"}
                  </span>
                  <span>预计 {formatDate(item.dueTime)}</span>
                  <RightOutlined />
                </footer>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="contentDueRiskPanel__empty">
          <CalendarOutlined />
          <strong>{items.length ? "当前筛选下暂无内容" : "暂无到期风险"}</strong>
          <span>{items.length ? "可切换其他状态继续查看" : "未来两天没有待完成内容，逾期内容也已清理完成。"}</span>
        </div>
      )}
    </Card>
  );
};

export default ContentDueRiskPanel;
